import React, { FC, memo, useState } from "react";

import Button from "../Button";
import { CustomizeButton } from "./PDPDetailsComponents";
import deepLink from "../DeepLink/hoc";

import useMobile from "../../hooks/useMobile";

import { buildCreateCardDeepLinkPath } from "../../utils/urls";
import { isIOSDevice } from "../../utils/browser";
import { canDeviceCustomizeCard } from "../../utils/cards";
import { emailDesign } from "../../utils/api";

const IOSCustomizationWarningBanner = (
  isMobile: boolean,
  isIOS: boolean,
  isMobileCustomizable: boolean
) => {
  // Let an iOS user know they can customize a card in app if applicable.
  // They can use the in app customization button.
  if (isMobile && isIOS && !isMobileCustomizable) {
    return (
      <div className="paragraph-xsmall relative border border-lapel bg-white p-4 mb-4">
        To send this invitation, continue in our iOS app or favorite this card
        to customize later on desktop.
      </div>
    );
  } else {
    return null;
  }
};

const CustomizeAppDeepLink = deepLink(CustomizeButton, {
  campaign: "pdp_customize",
  feature: "pdp",
});

export const CustomizeOptions = (
  id: string | undefined,
  customizeURL: string,
  customizeTrackingProps: any = {},
  isMobile: boolean,
  isIOS: boolean,
  isMobileCustomizable: boolean,
  productType: string,
  compositionId: string | undefined,
) => {
  const deepLinkData = {
    // Newer versions (~7.19.1 and greater) use $deeplink_path
    // See PagesRN/App/NativeModules/Branch for typings
    $deeplink_path: buildCreateCardDeepLinkPath(id || '', compositionId || ''),
    // Older versions of the iOS app use cardPackage
    cardPackage: id,
  };

  if (canDeviceCustomizeCard(isMobile, isIOS, isMobileCustomizable)) {
    return (
      <div className="flex justify-evenly text-center items-baseline space-x-4">
        {productType !== "flyer" && isIOS && (
          <CustomizeAppDeepLink
            linkId={id}
            className="flex-grow"
            data={deepLinkData}
            authRequired={false}
            customizeInApp
          >
            Customize in App
          </CustomizeAppDeepLink>
        )}
        {(productType === "flyer" || !isMobile || isMobileCustomizable) && (
          <CustomizeButton
            className="flex-grow"
            href={customizeURL}
            tracking={customizeTrackingProps}
          >
            { 
              productType !== "flyer" || customizeTrackingProps?.ispremium ? "Customize" : "Customize for free"
            }
          </CustomizeButton>
        )}
      </div>
    );
  } else {
    return null;
  }
};

export const EmailThisDesign = (
  id: string,
  isMobile: boolean,
  isIOS: boolean,
  isMobileCustomizable: boolean
) => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleChange = (e: any) => setEmail(e.target.value);
  const handleSubmit = (e: any) => {
    e.preventDefault();

    emailDesign({ cardId: id, email_address: e.target[0].value });
    setIsSubmitted(true);
  };

  // Show email prompt if we're on a non-iOS device...
  if (isMobile && !isMobileCustomizable && !isIOS) {
    return (
      <div className="border border-alto p-4">
        <h3 className="tracked-medium text-center">Customize later</h3>
        <p className="paragraph-small pt-4 pb-5 text-center">
          Customization is not available on our mobile website. To save this
          design for later, email it to yourself or save it to your favorites.
        </p>
        <div>
          {isSubmitted ? (
            <div className="bg-success-background text-success paragraph-small p-6 text-center">
              Card design was sent to <strong>{email}</strong>
            </div>
          ) : (
            <form className="flex" action="." onSubmit={handleSubmit}>
              <input
                className="border w-3/4 flex-grow border-alto border-r-0 bg-none flex-grow paragraph-small pl-2 placeholder-silver::placeholder"
                type="email"
                placeholder="Enter your email"
                onChange={handleChange}
                required
              />
              <Button
                className="grow-0 w-1/4 bg-lapel text-white tracked-medium"
                type="submit"
              >
                Send
              </Button>
            </form>
          )}
        </div>
      </div>
    );
  } else {
    return null;
  }
};

interface Props {
  className?: string;
  id?: string;
  compositionId: string;
  customizeURL: string;
  customizeTrackingProps: any;
  isMobileCustomizable: boolean;
  productType: string;
}

const MobileCardCustomizeActions: FC<Props> = (props) => {
  const { id,compositionId, customizeURL, customizeTrackingProps, isMobileCustomizable, productType } = props;
  const isMobile = useMobile();
  const isIOS = isIOSDevice();

  return (
    <div className="flex flex-col">
      {IOSCustomizationWarningBanner(isMobile, isIOS, isMobileCustomizable)}
      {CustomizeOptions(
        id,
        customizeURL,
        customizeTrackingProps,
        isMobile,
        isIOS,
        isMobileCustomizable,
        productType,
        compositionId
      )}
      {id && EmailThisDesign(id, isMobile, isIOS, isMobileCustomizable)}
    </div>
  );
};

export default memo(MobileCardCustomizeActions);
